import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ProfileResponse } from '../../types/profile'
import '../../styles/chat-user-popover.css'

type Certificate = {path:string;title:string;code:string|null;issued_date:string|null}
type CardData = {profile:ProfileResponse;role:string;certificates:Certificate[]}
type OpenDetail = {name:string;rect:{left:number;right:number;top:number;bottom:number}|null}
const cache = new Map<string,{value:CardData;at:number}>()
const TTL = 90_000
export function openChatUserCard(name:string, anchor?:HTMLElement|null){
  const r=anchor?.getBoundingClientRect()
  window.dispatchEvent(new CustomEvent<OpenDetail>('kraken-open-chat-user',{detail:{name,rect:r?{left:r.left,right:r.right,top:r.top,bottom:r.bottom}:null}}))
}
function position(rect:OpenDetail['rect']){
  const width=Math.min(350,window.innerWidth-20), height=Math.min(540,window.innerHeight-20), gap=12
  if(!rect)return {left:Math.max(10,(window.innerWidth-width)/2),top:Math.max(10,(window.innerHeight-height)/2)}
  const rightSpace=window.innerWidth-rect.right
  const left=rightSpace>=width+gap?rect.right+gap:rect.left>=width+gap?rect.left-width-gap:Math.max(10,Math.min(rect.left,window.innerWidth-width-10))
  const top=Math.max(10,Math.min(rect.top-22,window.innerHeight-height-10))
  return {left,top}
}
export default function ChatUserPopover(){
  const [open,setOpen]=useState<OpenDetail|null>(null)
  const [data,setData]=useState<CardData|null>(null)
  const [loading,setLoading]=useState(false)
  const [readyName,setReadyName]=useState<string|null>(null)
  const [error,setError]=useState('')
  const [pos,setPos]=useState({left:10,top:10})
  const cardRef=useRef<HTMLElement>(null)
  useEffect(()=>{
    const onOpen=(event:Event)=>{const d=(event as CustomEvent<OpenDetail>).detail;setReadyName(null);setOpen(d);setPos(position(d.rect))}
    window.addEventListener('kraken-open-chat-user',onOpen)
    return ()=>window.removeEventListener('kraken-open-chat-user',onOpen)
  },[])
  useEffect(()=>{
    if(!open)return
    const cached=cache.get(open.name)
    if(cached && Date.now()-cached.at<TTL){setData(cached.value);setReadyName(open.name);setLoading(false);setError('');return}
    const controller=new AbortController()
    setLoading(true);setError('');setData(null);setReadyName(null)
    fetch(`/api/v1/profile/${encodeURIComponent(open.name)}/chat-card`,{credentials:'include',signal:controller.signal})
      .then(async r=>{if(!r.ok)throw Error(r.status===404?'Профиль недоступен':'Не удалось загрузить профиль');return r.json()})
      .then((value:CardData)=>{cache.set(open.name,{value,at:Date.now()});setData(value);setReadyName(open.name)})
      .catch(e=>{if(!controller.signal.aborted)setError(e.message)})
      .finally(()=>{if(!controller.signal.aborted)setLoading(false)})
    return ()=>controller.abort()
  },[open?.name])
  useLayoutEffect(()=>{if(open)setPos(position(open.rect))},[open,data])
  useEffect(()=>{
    if(!open)return
    const onKey=(e:KeyboardEvent)=>{if(e.key==='Escape')setOpen(null)}
    const onPointer=(e:PointerEvent)=>{if(cardRef.current&&!cardRef.current.contains(e.target as Node))setOpen(null)}
    const onScroll=()=>setOpen(null)
    const onResize=()=>setPos(position(open.rect))
    document.addEventListener('pointerdown',onPointer,true)
    document.addEventListener('keydown',onKey)
    window.addEventListener('scroll',onScroll,true)
    window.addEventListener('resize',onResize)
    return ()=>{document.removeEventListener('pointerdown',onPointer,true);document.removeEventListener('keydown',onKey);window.removeEventListener('scroll',onScroll,true);window.removeEventListener('resize',onResize)}
  },[open])
  if(!open)return null
  const currentData=readyName===open.name&&!loading&&!error?data:null
  const p=currentData?.profile, user=p?.user, certificates=currentData?.certificates||[]
  const role=currentData?.role==='admin'?'Администрация':currentData?.role==='moderator'?'Модератор':null
  const showcase=p?.award_showcase?.selected?.filter(x=>x.award).map(x=>x.award!)||[]
  return <section ref={cardRef} className="kcp-card kcp-floating" role="dialog" aria-modal="false" aria-label={`Профиль ${open.name}`} style={{left:pos.left,top:pos.top}}>
    <button type="button" className="kcp-close" onClick={()=>setOpen(null)} aria-label="Закрыть">×</button>
    {!p&&!error&&<div className="kcp-skeleton" role="status" aria-live="polite" aria-label="Загружаем профиль пользователя">
      <div className="kcp-skeleton-cover kcp-shimmer"/>
      <div className="kcp-skeleton-body">
        <div className="kcp-skeleton-identity"><div className="kcp-skeleton-avatar kcp-shimmer"/><div className="kcp-skeleton-names"><div className="kcp-skeleton-line kcp-shimmer" style={{width:'72%'}}/><div className="kcp-skeleton-line kcp-shimmer" style={{width:'48%'}}/></div></div>
        <div className="kcp-skeleton-badges">{[0,1,2,3].map(i=><div key={i} className="kcp-skeleton-badge kcp-shimmer"/>)}</div>
        <div className="kcp-skeleton-line kcp-shimmer" style={{width:'94%'}}/><div className="kcp-skeleton-line kcp-shimmer" style={{width:'63%'}}/>
        <div className="kcp-skeleton-stats">{[0,1,2].map(i=><div key={i} className="kcp-skeleton-stat kcp-shimmer"/>)}</div>
        <div className="kcp-skeleton-action kcp-shimmer"/>
        <span className="kcp-sr-only">Загружаем профиль…</span>
      </div>
    </div>}
    {error&&!p&&<p className="kcp-state">{error}</p>}
    {p&&user&&<>
      <div className="kcp-cover" style={user.background?{backgroundImage:`linear-gradient(0deg,rgba(13,18,30,.65),transparent),url("${user.background}")`}:undefined}/>
      <div className="kcp-inner">
        <div className="kcp-identity">
          <div className="kcp-avatar">{user.avatar?<img src={user.avatar} alt=""/>:<span>{user.name.slice(0,1).toUpperCase()}</span>}{p.border.url&&<img className="kcp-border" src={p.border.url} alt=""/>}</div>
          <div className="kcp-user"><h3>{user.name}</h3>{role&&<strong className="kcp-role">{role}</strong>}{p.level.title&&<small>{p.level.title}</small>}</div>
        </div>
        {(showcase.length>0||certificates.length>0)&&<div className="kcp-badges" aria-label="Достижения и сертификации">
          {showcase.slice(0,5).map(a=><span className="kcp-badge" title={a.name} key={`award-${a.id}`}>{a.icon_url?<img src={a.icon_url} alt={a.name}/>:<span>★</span>}</span>)}
          {certificates.slice(0,5).map(c=><span className="kcp-badge kcp-certificate" title={`Сертификация: ${c.title}${c.code?` (${c.code})`:''}`} key={`cert-${c.path}`}><span>✦</span><small>{c.code||'CERT'}</small></span>)}
          {certificates.length>5&&<span className="kcp-badge kcp-extra" title="Остальные сертификаты доступны в профиле">+{certificates.length-5}</span>}
        </div>}
        {user.bio&&<p className="kcp-bio">{user.bio}</p>}
        <div className="kcp-stats">
          <div><strong>{p.level.level}</strong><small>Уровень</small></div>
          <div><strong>{p.stats.rank!=null?`#${p.stats.rank}`:'—'}</strong><small>Рейтинг</small></div>
          <div><strong>{p.stats.reputation??0}</strong><small>Карма</small></div>
        </div>
        <a className="kcp-open" href={`/profile/${encodeURIComponent(user.name)}`}>{p.permissions.can_edit?'Редактировать профиль':'Открыть профиль'}</a>
      </div>
    </>}
  </section>
}
